import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ExternalLink, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf623078-725f-4e36-b08f-8b0b55ed25bb/artifacts/jewke8hu_image.png";

// Configuration
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'sso';
// RÈGLE 6: Ne JAMAIS coder en dur l'URL - utiliser une variable d'environnement
const CENTRALE_URL = process.env.REACT_APP_CENTRALE_URL;

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  
  // États pour le mode local (identifiant = users.username dans prysma_bdd)
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Si déjà connecté, rediriger vers l'accueil
    if (user) {
      navigate('/');
      return;
    }
    
    // Détecter si un token SSO est présent dans l'URL (envoyé par la Centrale)
    // La Centrale peut utiliser différents noms de paramètres
    const ssoToken = searchParams.get('token') 
      || searchParams.get('sso_token') 
      || searchParams.get('jwt') 
      || searchParams.get('auth_token')
      || searchParams.get('t');
    
    // Debug: afficher tous les paramètres reçus
    console.log('SSO Debug - URL params:', Object.fromEntries(searchParams.entries()));
    console.log('SSO Debug - Token found:', ssoToken ? 'Yes' : 'No');
    
    if (ssoToken) {
      console.log('SSO Debug - Redirecting to /sso');
      // Rediriger vers /sso avec le token pour traitement
      navigate(`/sso?token=${encodeURIComponent(ssoToken)}`, { replace: true });
    } else if (AUTH_MODE === 'sso' && CENTRALE_URL) {
      // MODE SSO: Pas de token = redirection automatique vers la Centrale
      // L'utilisateur ne peut pas accéder directement à l'app sans passer par la Centrale
      console.log('SSO Debug - No token, redirecting to Centrale');
      window.location.href = CENTRALE_URL;
    }
  }, [user, navigate, searchParams]);

  // Handler pour le login local
  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(username, password);
      toast.success('Connexion réussie');
      navigate('/');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 422 && Array.isArray(detail)) {
        toast.error(detail.map((e) => e.msg || e.type).filter(Boolean).join(' · ') || 'Requête invalide');
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.message || JSON.stringify(detail));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else if (error.code === 'ECONNABORTED' || !error.response) {
        toast.error('Serveur injoignable — vérifiez que le backend est démarré puis réessayez');
      } else {
        toast.error(status === 503 ? 'Service temporairement indisponible' : 'Identifiants incorrects');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Redirection vers la Centrale Prysma
  const handleRedirectToCentrale = () => {
    // RÈGLE 6: Vérifier que l'URL est configurée
    if (!CENTRALE_URL) {
      toast.error('URL de la Centrale non configurée. Contactez l\'administrateur.');
      return;
    }
    // L'URL de callback où la Centrale renverra l'utilisateur
    const callbackUrl = `${window.location.origin}/sso`;
    // Redirection vers la Centrale avec l'URL de callback
    window.location.href = `${CENTRALE_URL}?callback=${encodeURIComponent(callbackUrl)}`;
  };

  // ============================================
  // MODE LOCAL : Formulaire classique (Preview/Dev)
  // ============================================
  if (AUTH_MODE === 'local') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Prysm'Analytics" 
              className="w-20 h-20 mb-4"
            />
            <h1 className="font-heading text-3xl font-bold text-white">
              Prysm'Analytics
            </h1>
          </div>

          <Card className="bg-card border-border">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center text-white font-heading">
                Connexion
              </CardTitle>
              <CardDescription className="text-center text-zinc-400">
                Entrez vos identifiants pour accéder à la plateforme
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-zinc-300">Nom d&apos;utilisateur</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Identifiant (champ username)"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder-zinc-500"
                    required
                    autoComplete="username"
                    data-testid="login-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-zinc-800/50 border-zinc-700 text-white placeholder-zinc-500 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-zinc-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-zinc-400" />
                      )}
                    </Button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary text-black hover:bg-primary/90 font-semibold"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-zinc-500 mt-6">
            Mode développement - Authentification locale
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // MODE SSO : Redirection Centrale (Production)
  // ============================================
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Prysm'Analytics" 
            className="w-20 h-20 mb-4"
          />
          <h1 className="font-heading text-3xl font-bold text-white">
            Prysm'Analytics
          </h1>
        </div>

        <Card className="bg-card border-border">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-white font-heading">
              Accès sécurisé
            </CardTitle>
            <CardDescription className="text-center text-zinc-400">
              L'authentification se fait via la Centrale Prysma
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <p className="text-zinc-300 text-sm mb-2">
                Pour accéder à Prysm'Analytics, vous devez vous connecter via la Centrale d'Applications Prysma.
              </p>
              <p className="text-zinc-500 text-xs">
                Cliquez sur le bouton ci-dessous pour être redirigé vers la page de connexion.
              </p>
            </div>

            <Button 
              onClick={handleRedirectToCentrale}
              className="w-full bg-primary text-black hover:bg-primary/90 font-semibold h-12"
              data-testid="sso-redirect-btn"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Connexion via Centrale Prysma
            </Button>

            <div className="text-center">
              <p className="text-xs text-zinc-500">
                Vous serez redirigé vers la Centrale pour vous authentifier,
                <br />puis automatiquement connecté à l'application.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Authentification SSO - Centrale Prysma
        </p>
      </div>
    </div>
  );
};

export default Login;
